import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, Button } from '../components/ui';
import { Shield, Zap, Globe, Heart, Mail, MapPin, Building2 } from 'lucide-react';

const About = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl mb-4">
            INK<span className="text-primary">TRACK</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Dovme sanati icin gelistirilmis yapay zeka destekli bakim ve koleksiyon yonetim platformu.
          </p>
        </motion.div>

        {/* Mission */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <Card className="p-8">
            <h2 className="font-heading text-2xl mb-4 text-primary">Misyonumuz</h2>
            <p className="text-muted-foreground leading-relaxed">
              InkTrack, dovme sanatini seven herkesin koleksiyonunu dijital ortamda yonetmesini,
              dovmelerinin bakimini takip etmesini ve yapay zeka destekli saglik analizleri ile
              dovmelerinin en iyi durumda kalmasini saglamak icin kurulmustur. Dunyada 100 milyondan
              fazla dovme sahibi icin dijital bir asistan olmak istiyoruz.
            </p>
          </Card>
        </motion.div>

        {/* Features */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {[
            { icon: Shield, title: 'AI Smart Check', desc: 'Yapay zeka ile dovmenizin saglik durumunu analiz edin. Enfeksiyon, alerji ve iyilesme takibi.' },
            { icon: Zap, title: 'AR Onizleme', desc: 'Arttirilmis gerceklik ile dovme tasariminizi vucudunuzda canli olarak onizleyin.' },
            { icon: Globe, title: '7 Dil Destegi', desc: 'Turkce, Ingilizce, Almanca, Ispanyolca, Fransizca, Italyanca ve Portekizce.' },
            { icon: Heart, title: 'Bakim Takibi', desc: 'Gunluk bakim rutininizi olusturun, hatirlaticilar alin ve iyilesme surecini izleyin.' },
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
            >
              <Card className="p-6 h-full">
                <feature.icon className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-heading text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Company Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <Card className="p-8">
            <h2 className="font-heading text-2xl mb-6 text-primary">Sirket Bilgileri</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium">CerebrAI VorTX</p>
                    <p className="text-sm text-muted-foreground">Yazilim ve Yapay Zeka Cozumleri</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium">info@cerebrai-vortx.com</p>
                    <p className="text-sm text-muted-foreground">Genel Iletisim</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium">cerebrai-vortx.com</p>
                    <p className="text-sm text-muted-foreground">Kurumsal Web Sitesi</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium">Turkiye</p>
                    <p className="text-sm text-muted-foreground">Merkez Ofis</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Legal Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center space-y-4"
        >
          <div className="flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="text-primary hover:underline">Gizlilik Politikasi</Link>
            <Link to="/terms" className="text-primary hover:underline">Kullanim Sartlari</Link>
          </div>
          {/* Payment Logos */}
          <div className="flex items-center justify-center gap-6 pt-6 border-t border-border mt-6">
            <img src="/images/iyzico-footer.svg" alt="iyzico ile ode" className="h-8 opacity-70" />
            <div className="flex items-center gap-2">
              <svg className="h-8 w-12" viewBox="0 0 48 32" fill="none"><rect width="48" height="32" rx="4" fill="#1A1F71"/><path d="M18.5 21.5h-3l1.9-11h3l-1.9 11zm12.4-10.7c-.6-.2-1.5-.5-2.7-.5-3 0-5.1 1.5-5.1 3.7 0 1.6 1.5 2.5 2.6 3 1.1.6 1.5.9 1.5 1.4 0 .8-.9 1.1-1.7 1.1-1.1 0-1.8-.2-2.7-.6l-.4-.2-.4 2.4c.7.3 1.9.6 3.2.6 3.2 0 5.2-1.5 5.2-3.8 0-1.3-.8-2.2-2.5-3-.9-.5-1.5-.9-1.5-1.4 0-.5.5-1 1.5-1 .9 0 1.5.2 2 .4l.2.1.4-2.2zm7.9-.3h-2.3c-.7 0-1.3.2-1.6.9l-4.4 10.1h3.2l.6-1.7h3.8l.4 1.7h2.8l-2.5-11zm-3.6 7.1l1.2-3.1.5-1.3.3 1.3.7 3.1h-2.7zM16.3 10.5l-2.8 7.5-.3-1.5c-.5-1.7-2.1-3.5-3.9-4.4l2.7 9.9h3.2l4.8-11.5h-3.7z" fill="white"/><path d="M10.5 10.5H5.6l-.1.3c3.8.9 6.3 3.2 7.4 5.9l-1.1-5.3c-.2-.7-.7-.9-1.3-.9z" fill="#F9A825"/></svg>
              <svg className="h-8 w-12" viewBox="0 0 48 32" fill="none"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="8" fill="#EB001B"/><circle cx="29" cy="16" r="8" fill="#F79E1B"/><path d="M24 10.3a8 8 0 0 1 0 11.4 8 8 0 0 1 0-11.4z" fill="#FF5F00"/></svg>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} CerebrAI VorTX. Tum haklari saklidir.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
